import type { QueryClient } from '@tanstack/react-query'
import { Link, Outlet, createRootRouteWithContext, useRouterState } from '@tanstack/react-router'
import type { auth } from '../lib/auth'
import { useSession } from '../lib/auth'
import { isFlagOn } from '../lib/posthog'
import { todayAtFacility } from '../lib/time'
import { BottomTabs } from '../components/BottomTabs'
import { useIsAdmin } from '../lib/admin'

/** Everything the router makes available to loaders and `beforeLoad` guards. */
export interface RouterContext {
  queryClient: QueryClient
  auth: typeof auth
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
})

function RootLayout() {
  const session = useSession()
  const isAdmin = useIsAdmin()
  const customerAppOpen = isFlagOn('customer-app')

  // The admin area brings its own header and a wider measure. Matched by route
  // id rather than URL prefix, so it cannot drift if a path is renamed.
  const inAdminArea = useRouterState({
    select: (state) => state.matches.some((match) => match.routeId === '/_authed/_admin'),
  })
  if (inAdminArea) return <Outlet />

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        {/* Hidden below `md`, where seven links do not fit and the bottom tab
            bar takes over. The wordmark stays visible at every width. */}
        <nav className="mx-auto flex max-w-3xl flex-wrap items-center gap-x-6 gap-y-2 px-6 py-4">
          <Link to="/" className="inline-flex min-h-11 items-center font-semibold">
            Golf Bay Manager
          </Link>
          {/* Hidden while the coaches run the schedule by hand. Hiding only --
              Supabase signups are what stop somebody booking, because the API
              answers to a token and not to this nav. */}
          {customerAppOpen && (
            <Link
              to="/book"
              search={{ date: todayAtFacility() }}
              className="hidden text-slate-600 hover:text-slate-900 md:inline"
            >
              Reservar
            </Link>
          )}
          {/* Outside the signed-in block on purpose: somebody who has never
              logged in should be able to see that there is a tournament next
              month before deciding whether this place is for them. */}
          <Link to="/tournaments" className="hidden text-slate-600 hover:text-slate-900 md:inline">
            Torneos
          </Link>
          {session ? (
            <>
              {customerAppOpen && (
                <Link
                  to="/bookings"
                  search={{ tab: 'upcoming' }}
                  className="hidden text-slate-600 hover:text-slate-900 md:inline"
                >
                  Mis reservas
                </Link>
              )}
              {customerAppOpen && (
                <Link
                  to="/packages"
                  className="hidden text-slate-600 hover:text-slate-900 md:inline"
                >
                  Comprar horas
                </Link>
              )}
              <Link to="/me" className="hidden text-slate-600 hover:text-slate-900 md:inline">
                Mi cuenta
              </Link>
              {/* Only for admins: no customer should see a door they cannot
                  open. The backend enforces access regardless. */}
              {isAdmin && (
                <Link
                  to="/admin/today"
                  className="hidden rounded-full bg-slate-900 px-3 py-1 text-sm text-white md:inline"
                >
                  Local
                </Link>
              )}
            </>
          ) : (
            <Link
              to="/login"
              search={{ redirect: undefined }}
              className="hidden text-slate-600 hover:text-slate-900 md:inline"
            >
              Iniciar sesión
            </Link>
          )}
        </nav>
      </header>
      {/* pb-28 clears the fixed tab bar. Without it the last button on every
          page sits underneath it and cannot be tapped. */}
      <main className="mx-auto max-w-3xl px-6 pb-28 pt-8 md:pb-10 md:pt-10">
        <Outlet />
      </main>
      <BottomTabs />
    </div>
  )
}
